import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, List, Inbox, FileText, Bell, BarChart3,
  ChevronLeft, ChevronRight, Radar, Radio, Users, Building2, Globe, FileCode2, Tag, TrendingUp, Key
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { Tab } from '@/types';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  counts?: Partial<Record<Tab, number | undefined>>;
}

const navItems: { path: string; countKey?: Tab; label: string; icon: React.ElementType; description: string }[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, description: 'Pipeline overview' },
  { path: '/events', countKey: 'relevant', label: 'Events', icon: List, description: 'Relevant decisions' },
  { path: '/events/raw', countKey: 'raw', label: 'Raw Events', icon: Inbox, description: 'Collected events' },
  { path: '/live', countKey: 'live', label: 'Live Feed', icon: Radio, description: 'Real-time activity' },
  { path: '/coverage', countKey: 'cover', label: 'Coverage', icon: FileText, description: 'Coverage docs' },
  { path: '/alerts', countKey: 'alerts', label: 'Alerts', icon: Bell, description: 'Alert history' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, description: 'Trends & stats' },
];

const adminItems: { path: string; label: string; icon: React.ElementType; description: string; minRole: 'admin' | 'super_admin' }[] = [
  { path: '/admin/users', label: 'Users', icon: Users, description: 'Manage users', minRole: 'admin' },
  { path: '/admin/agents', label: 'Agents', icon: Building2, description: 'Manage agents', minRole: 'admin' },
  { path: '/admin/categories', label: 'Categories', icon: Tag, description: 'Coverage categories', minRole: 'admin' },
  { path: '/admin/analytics', label: 'ROI', icon: TrendingUp, description: 'Cost analysis & ROI', minRole: 'admin' },
  { path: '/admin/api-keys', label: 'API Keys', icon: Key, description: 'Agent API keys', minRole: 'admin' },
  { path: '/admin/orgs', label: 'Organisations', icon: Globe, description: 'Manage orgs', minRole: 'super_admin' },
  { path: '/docs', label: 'API Docs', icon: FileCode2, description: 'API reference', minRole: 'super_admin' },
];

export function Sidebar({
  collapsed,
  onToggleCollapse,
  counts,
}: SidebarProps) {
  const { hasRole } = useAuth();

  const visibleAdminItems = adminItems.filter(item => hasRole(item.minRole));

  return (
    <motion.aside
      className={cn(
        'relative flex flex-col h-screen border-r border-border/50 bg-sidebar shrink-0 z-40',
      )}
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 shrink-0">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sabio-purple to-indigo-600 flex items-center justify-center shadow-lg shadow-sabio-purple/20 shrink-0">
          <Radar className="w-5 h-5 text-white" />
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <h1 className="text-lg font-bold bg-gradient-to-r from-sabio-purple-light to-sabio-yellow bg-clip-text text-transparent whitespace-nowrap">
                Sentinel
              </h1>
              <p className="text-[10px] text-muted-foreground whitespace-nowrap">Event Intelligence</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Separator className="opacity-50" />

      {/* Navigation */}
      <ScrollArea className="flex-1 py-3">
        <nav className="px-2 space-y-1">
          {navItems.map(item => {
            const count = item.countKey ? counts?.[item.countKey] : undefined;
            const Icon = item.icon;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end
                className={({ isActive }) => cn(
                  'w-full flex items-center gap-3 rounded-lg transition-all duration-150 group relative',
                  collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
                  isActive
                    ? 'bg-primary/10 text-primary dark:bg-primary/15'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                )}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.div
                        layoutId="sidebar-active"
                        className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-primary rounded-r-full"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                      />
                    )}

                    <Icon className={cn('w-5 h-5 shrink-0', isActive && 'text-primary')} />

                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex-1 text-sm font-medium text-left whitespace-nowrap"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>

                    {!collapsed && count !== undefined && count > 0 && (
                      <Badge
                        variant={isActive ? 'default' : 'secondary'}
                        className="text-[10px] h-5 min-w-5 px-1.5"
                      >
                        {count > 99 ? '99+' : count}
                      </Badge>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Admin section */}
        {visibleAdminItems.length > 0 && (
          <>
            <Separator className="opacity-50 my-3 mx-2" />
            {!collapsed && (
              <p className="px-4 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Administration
              </p>
            )}
            <nav className="px-2 space-y-1">
              {visibleAdminItems.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => cn(
                      'w-full flex items-center gap-3 rounded-lg transition-all duration-150 group relative',
                      collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
                      isActive
                        ? 'bg-primary/10 text-primary dark:bg-primary/15'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <motion.div
                            layoutId="sidebar-active"
                            className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-primary rounded-r-full"
                            transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                          />
                        )}
                        <Icon className={cn('w-5 h-5 shrink-0', isActive && 'text-primary')} />
                        <AnimatePresence>
                          {!collapsed && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="flex-1 text-sm font-medium text-left whitespace-nowrap"
                            >
                              {item.label}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </>
        )}
      </ScrollArea>

      {/* Collapse Toggle */}
      <div className="p-2 shrink-0">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleCollapse}
          className="w-full"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </motion.aside>
  );
}
